import React from 'react';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { Retry } from '@src/components/common';
import {
  CollapseWrapper,
  Navig,
  StyledMenu,
  StyledNoAlert,
  StyledSider,
  StyledStatusText,
  StyledSubText,
  TriggerButton,
} from './styled';

interface SidebarSectionProps {
  collapsed: boolean;
  toggleCollapsed: () => void;
  sidebarLoading: boolean;
  sidebarItems: any[];
  filterItems: string[];
  defaultOpenCategories: string[];
  onSelectMenuItem: (params: { key: string; selectedKeys: string[] }) => void;
  onDeselectMenuItem: (params: { selectedKeys: string[] }) => void;
  currentCounts: any;
  hasActiveFilters: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

const SidebarEmpty: React.FC<{
  currentCounts: any;
  hasActiveFilters: boolean;
  isFetching: boolean;
  isError: boolean;
  sidebarLoading: boolean;
}> = ({ currentCounts, hasActiveFilters, isFetching, isError, sidebarLoading }) => {
  // Use currentCounts for empty state logic since sidebar counts are stable
  const hasValidAlerts = Object.values(currentCounts || {}).some((count: any) => count > 0);

  // Only show "all metrics green" when there are genuinely no alerts (not filtered)
  if (!hasValidAlerts && !isFetching && !isError && !sidebarLoading && !hasActiveFilters) {
    return (
      <StyledNoAlert>
        <StyledStatusText>All metrics are in green!</StyledStatusText>
        <StyledSubText>No alerts detected for the selected time period.</StyledSubText>
      </StyledNoAlert>
    );
  }
  return null;
};

const SidebarSection: React.FC<SidebarSectionProps> = ({
  collapsed,
  toggleCollapsed,
  sidebarLoading,
  sidebarItems,
  filterItems,
  defaultOpenCategories,
  onSelectMenuItem,
  onDeselectMenuItem,
  currentCounts,
  hasActiveFilters,
  isFetching,
  isError,
  refetch,
}) => {
  return (
    <StyledSider
      width="16%"
      collapsible
      collapsed={collapsed}
      onCollapse={toggleCollapsed}
      collapsedWidth={0}
      theme="light"
      trigger={null}
    >
      <CollapseWrapper>
        <Navig collapsed={collapsed}></Navig>
        <TriggerButton collapsed={collapsed} onClick={toggleCollapsed}>
          {collapsed ? <RightOutlined /> : <LeftOutlined />}
        </TriggerButton>
      </CollapseWrapper>

      <Retry
        auto={false}
        isLoading={sidebarLoading}
        showError={isError}
        fetchData={refetch}
        mountChildAlways={true}
        progress={false}
        isEmpty={false}
      >
        <StyledMenu
          mode="inline"
          theme="light"
          inlineCollapsed={collapsed}
          multiple
          items={sidebarItems}
          inlineIndent={8}
          selectedKeys={filterItems}
          defaultOpenKeys={defaultOpenCategories}
          onSelect={onSelectMenuItem}
          onDeselect={onDeselectMenuItem}
        />
        <SidebarEmpty
          currentCounts={currentCounts}
          hasActiveFilters={hasActiveFilters}
          isFetching={isFetching}
          isError={isError}
          sidebarLoading={sidebarLoading}
        />
      </Retry>
    </StyledSider>
  );
};

export default SidebarSection;
